import React, { useEffect, useState } from "react";
import { RefreshCw, Users, Wrench, ClipboardList, Mail, MapPin, Zap, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const auth = () => {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="fl-card p-4 bg-white/[0.02] border-white/10">
      <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest"><Icon className="w-3.5 h-3.5 text-emerald-400" /> {label}</div>
      <div className="text-3xl font-black text-white mt-1">{value ?? "—"}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

export default function FixoInsightsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data: d } = await api.get("/admin/fixo/stats", { headers: auth() });
      setData(d);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (busy && !data) return <div className="fl-card p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-emerald-400" /></div>;
  if (err) return <div className="fl-card p-6 border-red-400/30 text-red-300 text-sm">{err}</div>;
  if (!data) return null;

  const { users, triage, jobs, contact, top_zips, top_trades, recent_signups, recent_contact, recent_triage } = data;

  return (
    <div className="space-y-5" data-testid="fixo-insights-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="fl-display text-2xl font-bold">Fixo Insights</h2>
        <button onClick={load} disabled={busy} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="fixo-insights-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Top-level stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users}     label="Total signups" value={users.total} hint={`+${users.last_24h} last 24h · +${users.last_7d} last 7d`} />
        <Stat icon={Wrench}    label="Contractors"   value={users.contractors} hint={`${users.homeowners} homeowners`} />
        <Stat icon={Zap}       label="AI triage runs" value={triage.total} hint={`+${triage.last_24h} last 24h · +${triage.last_7d} last 7d`} />
        <Stat icon={ClipboardList} label="Jobs posted" value={jobs.total} hint={`${jobs.open} open · ${jobs.awarded} awarded · ${jobs.bids} bids`} />
      </div>

      {/* Top zips + trades */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="fl-card p-4 bg-white/[0.02] border-white/10">
          <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><MapPin className="w-3.5 h-3.5 text-emerald-400" /> Top ZIP codes</div>
          {top_zips.length === 0 ? <div className="text-slate-500 text-sm">No data yet.</div> : (
            <div className="grid grid-cols-2 gap-1.5">
              {top_zips.map(z => (
                <div key={z.zip} className="flex items-center justify-between text-sm border-b border-white/5 py-1"><span className="text-white/90 font-mono">{z.zip}</span><span className="text-emerald-400 font-semibold">{z.count}</span></div>
              ))}
            </div>
          )}
        </div>
        <div className="fl-card p-4 bg-white/[0.02] border-white/10">
          <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Wrench className="w-3.5 h-3.5 text-emerald-400" /> Trades — pros signed up</div>
          {top_trades.length === 0 ? <div className="text-slate-500 text-sm">No pros yet.</div> : (
            <div className="grid grid-cols-2 gap-1.5">
              {top_trades.map(t => (
                <div key={t.trade} className="flex items-center justify-between text-sm border-b border-white/5 py-1"><span className="text-white/90 capitalize">{t.trade}</span><span className="text-emerald-400 font-semibold">{t.count}</span></div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent signups */}
      <div className="fl-card p-4 bg-white/[0.02] border-white/10">
        <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Users className="w-3.5 h-3.5 text-emerald-400" /> Recent signups ({recent_signups.length})</div>
        {recent_signups.length === 0 ? <div className="text-slate-500 text-sm">No signups yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/50 text-xs uppercase tracking-widest"><tr><th className="text-left py-2">When</th><th className="text-left">Name</th><th className="text-left">Email</th><th className="text-left">Role</th><th className="text-left">ZIP</th></tr></thead>
              <tbody>
                {recent_signups.map(u => (
                  <tr key={u.user_id} className="border-t border-white/5">
                    <td className="py-2 text-white/50 font-mono text-[11px]">{(u.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="text-white/90">{u.display_name || "—"}</td>
                    <td className="text-white/70">{u.email || "—"}</td>
                    <td><span className={`fl-mono text-[10px] uppercase px-1.5 py-0.5 rounded ${u.role === "contractor" ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-500/20 text-emerald-300"}`}>{u.role || "—"}</span></td>
                    <td className="text-white/60 font-mono text-xs">{u.zip_code || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contact form inbox */}
      <div className="fl-card p-4 bg-white/[0.02] border-white/10">
        <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Mail className="w-3.5 h-3.5 text-emerald-400" /> Contact inbox ({contact.total} total · +{contact.last_7d} last 7d)</div>
        {recent_contact.length === 0 ? <div className="text-slate-500 text-sm">No messages yet.</div> : (
          <div className="space-y-2">
            {recent_contact.map(m => (
              <div key={m.contact_id} className="p-3 rounded-lg bg-white/[0.03] border border-white/5" data-testid={`fixo-contact-${m.contact_id}`}>
                <div className="flex justify-between text-xs flex-wrap gap-2">
                  <span className="text-white/90 font-semibold">{m.name} <span className="text-white/50 font-normal">&lt;{m.email}&gt;</span></span>
                  <span className="text-white/40 font-mono">{(m.created_at || "").slice(0, 16).replace("T", " ")} {m.role && `· ${m.role}`}</span>
                </div>
                <div className="text-sm text-white/80 mt-1 whitespace-pre-wrap">{m.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent triage runs */}
      <div className="fl-card p-4 bg-white/[0.02] border-white/10">
        <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Zap className="w-3.5 h-3.5 text-emerald-400" /> Recent AI diagnoses ({recent_triage.length})</div>
        {recent_triage.length === 0 ? <div className="text-slate-500 text-sm">No triage runs yet.</div> : (
          <div className="space-y-1.5">
            {recent_triage.map(t => (
              <div key={t.triage_id} className="text-sm p-2 rounded bg-white/[0.02] border border-white/5">
                <div className="flex justify-between gap-2 flex-wrap text-xs">
                  <span className="text-emerald-300 fl-mono uppercase">{t.diagnosis?.trade || "—"} · {t.diagnosis?.severity || "—"}</span>
                  <span className="text-white/40 font-mono">{t.zip_code || "—"} · {(t.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-white/80 mt-1 line-clamp-2">{t.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-white/30 text-[10px] fl-mono uppercase tracking-widest">Generated {(data.generated_at || "").slice(0, 19).replace("T", " ")} UTC</div>
    </div>
  );
}
